// E15-t2 — THE EYEBROW LABEL, AND THE FIRST THING IN src/components/.
//
// WHERE THIS LIVES, AND WHY THE DIRECTORY IS NEW. Until this file the app had
// `src/pages/`, `src/shell/`, `src/api/` and `src/format/` and NO place for a
// piece of the visual language that belongs to no page — so the eyebrow would
// have had to be re-typed on Home, on Admin, on Dashboards and on the shell,
// which is how a design language stops being one. `src/components/` is that
// place: things the DESIGN SYSTEM does not ship and this product needs
// everywhere. E15 t3's chip and t4's evidence link belong here too.
//
// WHAT IT IS, from web/design/forge-prototype.html's own `.lbl` rule:
//
//     .lbl {
//       font-family: var(--mono); font-size: 10.5px; letter-spacing: .09em;
//       text-transform: uppercase; color: var(--faint); font-weight: 600;
//     }
//
// and from vault/Conventions/Forge Design Language.md: "the recurring motif …
// It NAMES A REGION WITHOUT COMPETING WITH IT." That is the whole design
// argument. A region needs a name; a heading gives it one and takes the reading
// order with it. The eyebrow is deliberately too small and too quiet to be read
// as a title, which is what lets a screen carry a dozen of them.
//
// FOUR OF THE FIVE DECLARATIONS ARE PROPS, NOT CSS, and that is on purpose —
// src/theme.ts is where a value lives, and a component that restated 10.5px
// would be the second place:
//
//   font-family  ← type="code"        → var(--font-family-code), the mono stack
//   font-size    ← size="3xs"         → var(--font-size-3xs) = 10.5
//   font-weight  ← weight="semibold"  → var(--font-weight-semibold) = 600
//   colour       ← color="disabled"   → var(--color-text-disabled) = the faint grey
//
// `type="code"` was checked rather than assumed: Astryx's Text applies
// `sizeByTypeStyles.code`, which carries `font-family: var(--font-family-code)`
// as well as `--text-code-size`, and the `size` prop is applied AFTER it in the
// same `stylex.props(...)` call, so `size="3xs"` overrides the 12.5 that `code`
// would otherwise set and the mono face survives. The theme's `--font-size-3xs`
// is the prototype's 10.5 (web/test/component/theme.test.ts pins it).
//
// ONLY LETTER-SPACING AND CASE ARE CSS, because Text has no prop for either.
// It is the same trade pages/device-approval.css already makes for
// `.dj-device-code` — one declaration of tracking on top of the design system's
// monospace face. `text-transform` rather than typed capitals is not a
// preference: the accessible name, `textContent`, and every assertion in
// web/test read the STRING, so uppercasing in the stylesheet keeps "Merge gate"
// one word to a screen reader and eight capitals to an eye.
import type { ReactNode } from 'react';

import { Text } from '@astryxdesign/core/Text';

import './eyebrow.css';

export interface EyebrowProps {
  /** the region's name, in ordinary sentence case — the CSS does the shouting */
  children: ReactNode;
  /** the element to render. `span` by default; `div` when it names a block. */
  as?: 'span' | 'div' | 'p';
  /** so an eyebrow can be the accessible name of the region under it via
   *  `aria-labelledby` — the reason to name a region is that something reads it */
  id?: string;
}

export function Eyebrow({ children, as = 'span', id }: EyebrowProps) {
  return (
    <Text
      as={as}
      type="code"
      size="3xs"
      weight="semibold"
      color="disabled"
      className="dj-eyebrow"
      id={id}
    >
      {children}
    </Text>
  );
}

export default Eyebrow;
